import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { COPY, CATEGORY_LABELS, categoryBg } from './copy';
import { impactOf, impactSummary } from '../shared/impact';

// The coach's proposals, each with Accept and Not now (PRD §6.5, FR6).
// Nothing is saved until the owner presses Accept; "Not now" tells the
// coach not to push it. The Actions panel lists the ones still waiting
// (narrow: the buttons sit under the text).
export default function ProposedActions({ proposals = [], onAccept, onDismiss, narrow = false }) {
  const [state, setState] = useState({}); // key -> 'accepting' | 'accepted' | 'dismissed' | 'error'

  const act = async (proposal, kind) => {
    setState((s) => ({ ...s, [proposal.key]: kind === 'accept' ? 'accepting' : 'dismissing' }));
    try {
      const ok = kind === 'accept' ? await onAccept(proposal) : await onDismiss(proposal);
      setState((s) => ({ ...s, [proposal.key]: ok ? (kind === 'accept' ? 'accepted' : 'dismissed') : 'error' }));
    } catch (e) {
      setState((s) => ({ ...s, [proposal.key]: 'error' }));
    }
  };

  const visible = proposals.filter((p) => state[p.key] !== 'dismissed');
  if (!visible.length) return null;

  return (
    <div role="list" aria-label="Proposed actions" className="flex flex-col gap-2.5">
      {visible.map((p) => {
        const status = state[p.key] || 'proposed';
        const accepted = status === 'accepted';
        return (
          <div
            role="listitem"
            key={p.key}
            data-testid={`proposal-${p.key}`}
            className={`flex flex-wrap items-center border rounded-xl gap-x-5 ${
              narrow ? 'p-3.5 gap-y-3' : 'py-4 px-[18px] gap-y-3.5'
            } ${accepted ? 'border-[hsl(var(--chart-2)/0.35)] bg-[hsl(var(--chart-2)/0.06)]' : 'border-border bg-card'}`}
          >
            <div className={`flex flex-col flex-1 gap-2 ${narrow ? 'basis-full' : 'min-w-[230px]'}`}>
              <div className="flex items-center flex-wrap gap-2.5">
                <span className="inline-flex items-center gap-1.5 font-label text-[10px] tracking-[0.06em] uppercase py-[3px] pr-[9px] pl-[7px] rounded-full border border-border text-foreground/85">
                  <span className={`w-[7px] h-[7px] rounded-full ${categoryBg(p.category)}`} />
                  {CATEGORY_LABELS[p.category] || p.category}
                </span>
                {impactSummary(impactOf(p)) ? (
                  <span className="font-label text-[11px] text-teal-ink tabular-nums" data-testid="proposal-impact">
                    {impactSummary(impactOf(p)).figure} · {impactSummary(impactOf(p)).label}
                  </span>
                ) : null}
              </div>
              {impactSummary(impactOf(p))?.detail && (
                <span className="text-[12px] leading-normal text-muted-foreground">{impactSummary(impactOf(p)).detail}</span>
              )}
              <span className={`${narrow ? 'text-[14px]' : 'text-[15px]'} font-medium leading-[1.45]`}>{p.title}</span>
              {p.description && <span className="text-[13px] leading-normal text-muted-foreground">{p.description}</span>}
            </div>
            {accepted ? (
              <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-teal-ink">
                <CheckCircle2 size={16} /> {COPY.inYourPlan}
              </span>
            ) : (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  className="min-h-9 px-[18px] rounded-full border-0 bg-orange-cta text-white text-[13px] font-medium cursor-pointer"
                  disabled={status === 'accepting'}
                  onClick={() => act(p, 'accept')}
                >
                  {status === 'accepting' ? 'Adding…' : COPY.accept}
                </button>
                <button
                  type="button"
                  className="min-h-9 px-3 rounded-full border-0 bg-transparent text-muted-foreground text-[13px] cursor-pointer"
                  disabled={status === 'accepting'}
                  onClick={() => act(p, 'dismiss')}
                >
                  {COPY.notNow}
                </button>
              </div>
            )}
            {status === 'error' && (
              <span className="w-full text-[12px] text-coral-ink">That didn't save. Please try again.</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
